import AlertDialog, {
  AlertDialogAction,
  AlertDialogCancel as AlertDialogCancelComponent,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@cobliteam/ui/components/AlertDialog';
import Checkbox from '@cobliteam/ui/form/Checkbox';
import Input from '@cobliteam/ui/form/Input';
import Label from '@cobliteam/ui/form/Label';
import { type ReactNode, useState } from 'react';

export interface AlertDialogDeleteInputChallenge {
  type: 'input';
  expectedValue: string;
  label: ReactNode;
  placeholder?: string;
}

export interface AlertDialogDeleteCheckboxChallenge {
  type: 'checkbox';
  label: ReactNode;
}

export type AlertDialogDeleteChallenge = AlertDialogDeleteInputChallenge | AlertDialogDeleteCheckboxChallenge;

export interface AlertDialogDeleteProps {
  open: boolean;
  title?: string;
  description?: string;
  confirmLabel?: string;
  challenge?: AlertDialogDeleteChallenge;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

const AlertDialogDelete = ({
  open,
  title = 'Excluir item?',
  description = 'Tem certeza que deseja excluir este item?\nEssa ação não poderá ser desfeita.',
  confirmLabel = 'Excluir',
  challenge,
  onOpenChange,
  onConfirm,
}: AlertDialogDeleteProps) => {
  const [inputValue, setInputValue] = useState('');
  const [isChecked, setIsChecked] = useState(false);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setInputValue('');
      setIsChecked(false);
    }
    onOpenChange(newOpen);
  };

  const handleConfirm = () => {
    onConfirm();
    setInputValue('');
    setIsChecked(false);
  };

  const isConfirmDisabled = challenge ? (challenge.type === 'input' ? inputValue !== challenge.expectedValue : !isChecked) : false;

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className='text-black'>{title}</AlertDialogTitle>
          <AlertDialogDescription className='whitespace-pre-line'>{description}</AlertDialogDescription>
          {challenge && (
            <div className='my-4'>
              {challenge.type === 'input' ? (
                <div className='flex flex-col gap-2'>
                  <Label htmlFor='input-challenge'>{challenge.label}</Label>
                  <Input id='input-challenge' value={inputValue} onChange={(e) => setInputValue(e.target.value)} placeholder={challenge.placeholder} />
                </div>
              ) : (
                <div className='flex gap-2'>
                  <Checkbox
                    id='checkbox-challenge'
                    checked={isChecked}
                    onCheckedChange={(checked) => setIsChecked(checked === 'indeterminate' ? false : checked === true)}
                    className='mt-1'
                  />
                  <Label htmlFor='checkbox-challenge'>{challenge.label}</Label>
                </div>
              )}
            </div>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancelComponent onClick={() => handleOpenChange(false)}>Cancelar</AlertDialogCancelComponent>
          <AlertDialogAction color='danger' disabled={isConfirmDisabled} onClick={handleConfirm}>
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default AlertDialogDelete;
